// File: src/components/forms/MultiLineField/MultiLineField.tsx
// Purpose: Form input component

import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { Mic, X } from "lucide-react";
import { TextField, TextFieldProps } from "../TextField";
import { Typography } from "../../general/Typography";
import { useSpeechRecognition } from "../../../hooks/useSpeechRecognition"; // adjust path
import { sanitizeAutoComplete } from "../../../utils/fieldSanitizers";
import { BaseFieldProps } from "../types/fieldTypes";

export interface MultiLineFieldProps extends TextFieldProps {
  minRows?: number;
  maxRows?: number;
  dictation?: boolean;
}

const TextArea = styled.textarea<{ minRows: number; maxRows: number }>`
  width: 100%;
  padding: ${({ theme }) => theme.spacing.sm};
  font-size: ${({ theme }) => theme.fonts.fontSize};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: ${({ theme }) => theme.colors.surfaceEntry};
  color: ${({ theme }) => theme.colors.textPrimary};
  transition: ${({ theme }) => theme.transitions.default};
  resize: none;
  overflow-y: auto;
  min-height: ${({ minRows }) => minRows * 24}px;
  max-height: ${({ maxRows }) => maxRows * 24}px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primaryHover};
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.primarySelected};
    outline: none;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.buttonDisabled};
    color: ${({ theme }) => theme.colors.textDisabled};
    cursor: not-allowed;
  }
`;

const InputWrapper = styled.div<{ fullWidth?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
    width: ${({ fullWidth }) => (fullWidth ? "100%" : "auto")};

`;

const IconButton = styled.button`
  position: absolute;
  bottom: 10px;
  right: 10px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: ${({ theme }) => theme.transitions.fast};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CharCounter = styled.span`
  position: absolute;
  bottom: 10px;
  right: 50px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MessageContainer = styled.div`
  min-height: ${({ theme }) => theme.spacing.lg};
  display: flex;
  align-items: center;
`;
const ErrorMessage = styled(Typography)`
  color: ${({ theme }) => theme.colors.error};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

const HelperMessage = styled(Typography)`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

type SpeechRecognitionType = typeof window.SpeechRecognition;


export const MultiLineField: React.FC<MultiLineFieldProps> = ({
  label,
  placeholder,
  minRows = 3,
  maxRows = 8,
  maxLength,
  dictation = false,
  value = "",
  overline,
  onChange,
  error,
  helperText,
  fullWidth,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const { isListening, start, stop } = useSpeechRecognition({
    lang: "en-US",
    onResult: (transcript) => {
      setInputValue((prev) => {
        const newText = prev.trim() + " " + transcript;
        return maxLength ? newText.slice(0, maxLength) : newText;
      });
    },
  });

  const handleDictation = () => {
    if (isListening) stop();
    else start();
  };

  return (
    <InputWrapper fullWidth={fullWidth}>
      {label && <Typography as="label" variant="body">{label}</Typography>}
      {overline && <Typography variant="overline" colorScheme="textSecondary">{overline}</Typography>}
      
      <TextArea
        {...props}
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          const newValue = e.target.value.slice(0, maxLength);
          setInputValue(newValue);
          onChange?.(e as React.ChangeEvent<HTMLTextAreaElement>);
        }}
        minRows={minRows}
        maxRows={maxRows}
        
        autoComplete={sanitizeAutoComplete(props.autoComplete)}

      />
      {maxLength && <CharCounter>{inputValue.length} / {maxLength}</CharCounter>}
      {dictation && (
        <IconButton onClick={handleDictation} title={isListening ? "Stop Dictation" : "Start Dictation"}>
          {isListening ? <X size={18} /> : <Mic size={18} />}
        </IconButton>
      )}
      <MessageContainer>
        {(!error && helperText) && <HelperMessage>{helperText}</HelperMessage>}
        {error && <ErrorMessage variant="caption" colorScheme="error">{error}</ErrorMessage>}
      </MessageContainer>
    </InputWrapper>
  );
};